// Deviation Alert Component
// Displays deviation warnings during task execution

import React, { useState } from 'react';
import { AlertTriangle, X, CheckCircle } from 'lucide-react';
import Modal from '../ui/Modal';
import './DeviationAlert.css';

const DeviationAlert = ({ 
  isOpen, 
  onClose, 
  deviation,
  onContinue,
  onPause
}) => {
  const [notes, setNotes] = useState('');
  
  if (!isOpen || !deviation) return null;
  
  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'critical': return '#dc3545';
      case 'major': return '#fd7e14';
      case 'minor': return '#ffc107';
      default: return '#6c757d';
    }
  };
  
  const getSeverityLabel = (severity) => {
    switch (severity) {
      case 'critical': return 'CRITICAL';
      case 'major': return 'MAJOR';
      case 'minor': return 'MINOR';
      default: return 'INFO';
    }
  };
  
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={
        <div className="deviation-alert-header">
          <AlertTriangle size={24} color={getSeverityColor(deviation.severity)} />
          <span>DEVIATION DETECTED</span>
        </div>
      }
      size="medium"
    >
      <div className="deviation-alert">
        {/* Severity Badge */}
        <div 
          className="severity-badge"
          style={{ backgroundColor: getSeverityColor(deviation.severity) }}
        >
          {getSeverityLabel(deviation.severity)}
        </div>
        
        {/* Deviation Details */}
        <div className="deviation-details">
          <div className="detail-row">
            <strong>Type:</strong>
            <span>{deviation.deviation_type || deviation.deviation_category || 'Unknown'}</span>
          </div>
          
          {deviation.step_number && (
            <div className="detail-row">
              <strong>Step:</strong>
              <span>Step {deviation.step_number}</span>
            </div>
          )}
          
          {deviation.action_number && (
            <div className="detail-row">
              <strong>Action:</strong>
              <span>Action {deviation.action_number}</span>
            </div>
          )}
          
          <div className="detail-description">
            <strong>Description:</strong>
            <p>{deviation.description}</p>
          </div>
          
          {deviation.actual_rpn_impact > 0 && (
            <div className="detail-row warning">
              <strong>RPN Impact:</strong>
              <span>+{deviation.actual_rpn_impact}</span>
            </div>
          )}
          
          {deviation.compliance_score_impact > 0 && (
            <div className="detail-row warning">
              <strong>Compliance Impact:</strong>
              <span>-{deviation.compliance_score_impact} points</span>
            </div>
          )}
        </div>
        
        {/* Immediate Actions */}
        {(deviation.severity === 'critical' || deviation.severity === 'major') && (
          <div className="immediate-actions">
            <strong>Immediate Action Required:</strong>
            <div className="action-checklist">
              <label>
                <input type="checkbox" />
                <span>Re-perform action correctly</span>
              </label>
              <label>
                <input type="checkbox" />
                <span>Document reason for deviation</span>
              </label>
              {deviation.severity === 'critical' && (
                <label>
                  <input type="checkbox" />
                  <span>Notify supervisor</span>
                </label>
              )}
            </div>
          </div>
        )}
        
        {/* Notes */}
        <div className="deviation-notes">
          <label>Notes:</label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Document reason for deviation or corrective action taken..."
            rows={4}
          />
        </div>
        
        {/* Actions */}
        <div className="deviation-actions">
          {onPause && (
            <button className="btn btn-secondary" onClick={onPause}>
              ⏸ Pause Task
            </button>
          )}
          <button 
            className="btn btn-primary" 
            onClick={() => onContinue && onContinue(notes)}
            style={{ 
              background: deviation.severity === 'critical' 
                ? 'linear-gradient(135deg, #dc3545 0%, #c82333 100%)'
                : undefined
            }}
          >
            <AlertTriangle size={16} /> Report & Continue
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default DeviationAlert;



